'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, UserPlus, X } from 'lucide-react';
import {
  ExistingPlayerSearch,
  UserAvatar,
  SEARCH_DEBOUNCE_MS,
} from './ExistingPlayerSearch';
import { EditModal } from '@/components/ui/edit-modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { getEloCategories } from '@/api/elo/eloApi';
import { userService } from '@/api/user/userApi';
import {
  managerRegisterTeam,
  type ManagerRegisterPlayerPayload,
  type ManagerRegisterTeamPayload,
} from '@/api/registrations/registrationsApi';
import { handleGlobalError } from '@/utils/error-handler';
import {
  managerRegisterPairSchema,
  validateBirthdateForAgeRestricted,
  type ManagerGender,
} from '@/schemas/managerRegisterPairSchema';
import { EloCategory } from '@/components/tournament/models/elo.model';
import {
  TOURNAMENT_AGE_MAX_UNRESTRICTED,
  TOURNAMENT_AGE_MIN_UNRESTRICTED,
} from '@/types/tournament.model';
import type { Availability } from '@/types/tournament.model';
import type { User } from '@/types/user.model';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  AvailabilityPicker,
  buildAvailabilityFromSelection,
  getMissingAvailabilityMessage,
  toSelection,
} from './AvailabilityPicker';
import type { FinalDayRule } from './final-day-rule';
import type { KnockoutStart } from '@/utils/tournament-schedule';
import { hasEloForSport } from './utils';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Slot = {
  query: string;
  selected: User | null;
  creating: boolean;
  newUserEmail: string;
  newUserName: string;
  newUserGender: ManagerGender | '';
  newUserBirthdate: string;
  categoryId: number | null;
  emailHasExistingUser: boolean;
};

interface RegisterPairModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournamentId: string;
  playersPerTeam: number;
  sportUid: string;
  minAge?: number;
  maxAge?: number;
  tournamentSchedule?: Availability[];
  /** What the last day is for: decides what the pair has to declare before it, and the picker's copy. */
  finalDayRule?: FinalDayRule;
  knockoutStart?: KnockoutStart | null;
  /** Emails already registered/playing in the tournament: excluded from the search so they can't be signed up twice. */
  registeredEmails?: string[];
  onRegistered: () => void;
}

// Single source of truth for "the manager still has to pick a category for this slot": the Save
// button, the payload check and the rendered picker all derive from it, so they cannot drift.
function slotNeedsCategory(slot: Slot, sportUid: string): boolean {
  return slot.selected !== null && !hasEloForSport(slot.selected, sportUid);
}

function makeEmptySlot(): Slot {
  return {
    query: '',
    selected: null,
    creating: false,
    newUserEmail: '',
    newUserName: '',
    newUserGender: '',
    newUserBirthdate: '',
    categoryId: null,
    emailHasExistingUser: false,
  };
}

export function RegisterPairModal({
  isOpen,
  onClose,
  tournamentId,
  playersPerTeam,
  sportUid,
  minAge,
  maxAge,
  tournamentSchedule,
  finalDayRule,
  knockoutStart,
  registeredEmails,
  onRegistered,
}: RegisterPairModalProps) {
  // El padre monta/desmonta este componente segun el estado del botón, asi que initialState alcanza:
  // no necesitamos un effect de reset que pueda pisar la edicion del usuario si la prop cambia.
  const [slots, setSlots] = useState<Slot[]>(() =>
    Array.from({ length: playersPerTeam }, makeEmptySlot),
  );
  const [allCategories, setAllCategories] = useState<EloCategory[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedByDate, setSelectedByDate] = useState<
    Record<string, Set<string>>
  >(() => toSelection(tournamentSchedule ?? []));

  const ageRestricted =
    (minAge !== undefined && minAge !== TOURNAMENT_AGE_MIN_UNRESTRICTED) ||
    (maxAge !== undefined && maxAge !== TOURNAMENT_AGE_MAX_UNRESTRICTED);

  // The manager registers teams outside the tournament's category range, so every category of the
  // sport is offered here (the backend skips the category check for manager registrations).
  const categories = useMemo(
    () => [...allCategories].sort((a, b) => a.minimumElo - b.minimumElo),
    [allCategories],
  );

  useEffect(() => {
    getEloCategories(sportUid)
      .then(setAllCategories)
      .catch((err) => handleGlobalError(err));
  }, [sportUid]);

  const updateSlot = (idx: number, patch: Partial<Slot>) => {
    setSlots((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    );
  };

  // emails to exclude from the typeahead: those already picked in other slots (avoids duplicates) + those
  // are already registered/playing in the tournament (they can't sign up again)
  const reservedEmails = useMemo(
    () =>
      new Set([
        ...(registeredEmails ?? []).map((e) => e.toLowerCase()),
        ...slots
          .map(
            (s) =>
              s.selected?.email.toLowerCase() ??
              (s.creating ? s.newUserEmail.toLowerCase() : null),
          )
          .filter((e): e is string => !!e),
      ]),
    [slots, registeredEmails],
  );

  const buildPayload = (): ManagerRegisterTeamPayload | null => {
    const players: ManagerRegisterPlayerPayload[] = [];

    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];

      if (slot.selected) {
        // A player with no Elo for this sport is rejected by the backend, so the manager picks the
        // category here and the account gets one on the spot.
        if (slotNeedsCategory(slot, sportUid) && slot.categoryId === null) {
          toast({
            title: `Jugador ${i + 1}: falta la categoría`,
            description:
              'Este jugador no tiene categoría cargada para el deporte del torneo. Elegile una.',
            variant: 'destructive',
          });
          return null;
        }
        // categoryId is null unless this slot asked for one, and the backend ignores it for a
        // player who already has Elo.
        players.push({
          email: slot.selected.email,
          categoryId: slot.categoryId ?? undefined,
        });
        continue;
      }

      if (!slot.creating) {
        toast({
          title: `Jugador ${i + 1}`,
          description: 'Elegí un jugador de la lista o creale una cuenta.',
          variant: 'destructive',
        });
        return null;
      }

      const slotInput = {
        email: slot.newUserEmail.trim().toLowerCase(),
        newUser: {
          name: slot.newUserName.trim(),
          gender: slot.newUserGender || undefined,
          birthdate: slot.newUserBirthdate || undefined,
        },
        categoryId: slot.categoryId ?? undefined,
      };

      const parsed = managerRegisterPairSchema.safeParse({
        players: [slotInput],
      });
      if (!parsed.success) {
        const issue = parsed.error.issues[0];
        toast({
          title: `Jugador ${i + 1}: revisá los datos`,
          description: issue?.message ?? 'Faltan campos requeridos.',
          variant: 'destructive',
        });
        return null;
      }

      const validSlot = parsed.data.players[0];

      const birthdateError = validateBirthdateForAgeRestricted(
        validSlot,
        ageRestricted,
      );
      if (birthdateError) {
        toast({
          title: `Jugador ${i + 1}: fecha de nacimiento`,
          description: birthdateError,
          variant: 'destructive',
        });
        return null;
      }

      players.push({
        email: validSlot.email,
        newUser: validSlot.newUser
          ? {
              email: validSlot.email,
              name: validSlot.newUser.name,
              gender: validSlot.newUser.gender,
              birthdate: validSlot.newUser.birthdate,
            }
          : undefined,
        categoryId: validSlot.categoryId,
      });
    }

    const emails = players.map((p) => p.email);
    if (new Set(emails).size !== emails.length) {
      toast({
        title: 'Emails duplicados',
        description: 'Los jugadores no pueden compartir email.',
        variant: 'destructive',
      });
      return null;
    }

    const availability = tournamentSchedule
      ? buildAvailabilityFromSelection(
          tournamentSchedule,
          selectedByDate,
          knockoutStart,
        )
      : undefined;

    const missingAvailability = getMissingAvailabilityMessage(
      tournamentSchedule ?? [],
      availability ?? [],
      finalDayRule,
      knockoutStart,
    );
    if (missingAvailability) {
      toast({
        title: 'Disponibilidad vacía',
        description: missingAvailability,
        variant: 'destructive',
      });
      return null;
    }

    return { players, availability };
  };

  const isSlotComplete = (slot: Slot): boolean => {
    if (slot.selected) {
      return !slotNeedsCategory(slot, sportUid) || slot.categoryId !== null;
    }
    if (!slot.creating) return false;
    if (slot.emailHasExistingUser) return false;
    const emailOk = EMAIL_RE.test(slot.newUserEmail.trim());
    const nameOk = slot.newUserName.trim().length > 0;
    const genderOk = slot.newUserGender !== '';
    const categoryOk = slot.categoryId !== null;
    const birthdateOk = !ageRestricted || slot.newUserBirthdate.length > 0;
    return emailOk && nameOk && genderOk && categoryOk && birthdateOk;
  };

  const canSave = slots.every(isSlotComplete);

  const handleSubmit = async () => {
    const payload = buildPayload();
    if (!payload) return;

    setSubmitting(true);
    try {
      await managerRegisterTeam(tournamentId, payload);
      toast({
        title: 'Pareja anotada',
        description: 'La inscripción quedó confirmada en el torneo.',
      });
      onRegistered();
      onClose();
    } catch (err) {
      handleGlobalError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Registrar equipo"
      onSave={handleSubmit}
      isLoading={submitting}
      disabled={!canSave}
      size="large"
      mobileFullScreen
    >
      <div className="flex max-h-[70vh] flex-col gap-6 overflow-y-auto py-4 max-sm:max-h-none">
        {slots.map((slot, idx) => (
          <SlotForm
            key={idx}
            index={idx}
            slot={slot}
            ageRestricted={ageRestricted}
            categories={categories}
            sportUid={sportUid}
            reservedEmails={reservedEmails}
            onChange={(patch) => updateSlot(idx, patch)}
          />
        ))}

        {tournamentSchedule && tournamentSchedule.length > 0 && (
          <AvailabilityPicker
            schedule={tournamentSchedule}
            selectedByDate={selectedByDate}
            onChange={setSelectedByDate}
            finalDayRule={finalDayRule}
            knockoutStart={knockoutStart}
          />
        )}
      </div>
    </EditModal>
  );
}

interface SlotFormProps {
  index: number;
  slot: Slot;
  ageRestricted: boolean;
  categories: EloCategory[];
  sportUid: string;
  reservedEmails: Set<string>;
  onChange: (patch: Partial<Slot>) => void;
}

function SlotForm({
  index,
  slot,
  ageRestricted,
  categories,
  sportUid,
  reservedEmails,
  onChange,
}: SlotFormProps) {
  const needsCategory = slotNeedsCategory(slot, sportUid);

  const handlePick = (user: User) => {
    onChange({
      selected: user,
      query: '',
      creating: false,
      // a category typed while creating an account must not carry over to the picked one
      categoryId: null,
    });
  };

  const handleStartCreate = () => {
    const trimmed = slot.query.trim();
    onChange({
      creating: true,
      newUserEmail: EMAIL_RE.test(trimmed) ? trimmed : '',
      query: '',
    });
  };

  const handleReset = () => {
    onChange({
      query: '',
      selected: null,
      creating: false,
      newUserEmail: '',
      newUserName: '',
      newUserGender: '',
      newUserBirthdate: '',
      categoryId: null,
      emailHasExistingUser: false,
    });
  };

  return (
    <div className="rounded-lg border border-border p-3 sm:p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">Jugador {index + 1}</h4>
        {(slot.selected || slot.creating) && (
          <Button type="button" variant="ghost" size="sm" onClick={handleReset}>
            <X className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Cambiar</span>
          </Button>
        )}
      </div>

      {slot.selected ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 rounded-md bg-green-50 p-3">
            <UserAvatar user={slot.selected} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-green-900">
                {slot.selected.name || slot.selected.email}
              </p>
              <p className="truncate text-xs text-green-700">
                {slot.selected.email}
              </p>
            </div>
            <Badge variant="secondary" className="hidden shrink-0 border border-green-300 bg-green-100 text-green-800 hover:bg-green-300 hover:text-green-900 sm:inline-flex">
              Usuario existente
            </Badge>
          </div>

          {needsCategory && (
            <div className="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <div>
                <p className="text-xs font-semibold text-amber-800">
                  Este jugador todavía no tiene categoría para este deporte
                </p>
                <p className="text-xs text-amber-700">
                  Nunca respondió las preguntas de nivel en la app. Elegile una
                  para poder anotarlo: sin ELO no se puede armar el fixture.
                </p>
              </div>
              <CategoryPicker
                index={index}
                categories={categories}
                value={slot.categoryId}
                onChange={(categoryId) => onChange({ categoryId })}
              />
            </div>
          )}
        </div>
      ) : !slot.creating ? (
        <SlotSearch
          index={index}
          query={slot.query}
          reservedEmails={reservedEmails}
          onQueryChange={(v) => onChange({ query: v })}
          onPick={handlePick}
          onStartCreate={handleStartCreate}
        />
      ) : (
        <NewUserForm
          index={index}
          slot={slot}
          ageRestricted={ageRestricted}
          categories={categories}
          onChange={onChange}
          onPick={handlePick}
        />
      )}
    </div>
  );
}

interface SlotSearchProps {
  index: number;
  query: string;
  reservedEmails: Set<string>;
  onQueryChange: (v: string) => void;
  onPick: (u: User) => void;
  onStartCreate: () => void;
}

function SlotSearch({
  index,
  query,
  reservedEmails,
  onQueryChange,
  onPick,
  onStartCreate,
}: SlotSearchProps) {
  return (
    <div className="flex flex-col gap-2">
      <ExistingPlayerSearch
        id={`search-${index}`}
        label="Buscar jugador existente"
        helper="Escribí el nombre o email. Si no tiene cuenta en GetMatch, podés crearla desde acá."
        placeholder="Nombre o email del jugador"
        query={query}
        onQueryChange={onQueryChange}
        reservedEmails={reservedEmails}
        onPick={onPick}
      />

      <div className="flex items-center gap-2 pt-1">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground">o</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onStartCreate}
        className="w-full"
      >
        <UserPlus className="mr-2 h-4 w-4" />
        Crear cuenta nueva
      </Button>
    </div>
  );
}

interface NewUserFormProps {
  index: number;
  slot: Slot;
  ageRestricted: boolean;
  categories: EloCategory[];
  onChange: (patch: Partial<Slot>) => void;
  onPick: (user: User) => void;
}

function NewUserForm({
  index,
  slot,
  ageRestricted,
  categories,
  onChange,
  onPick,
}: NewUserFormProps) {
  const debouncedEmail = useDebouncedValue(slot.newUserEmail.trim().toLowerCase(), SEARCH_DEBOUNCE_MS);
  const [existingUser, setExistingUser] = useState<User | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  // Stable ref so the email-check effect can call onChange without it being a dep
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  useEffect(() => {
    if (!EMAIL_RE.test(debouncedEmail)) {
      setExistingUser(null);
      onChangeRef.current({ emailHasExistingUser: false });
      return;
    }
    let cancelled = false;
    setCheckingEmail(true);
    userService
      .searchPlayers(debouncedEmail)
      .then((results) => {
        if (cancelled) return;
        const match = results.find((u) => u.email.toLowerCase() === debouncedEmail) ?? null;
        setExistingUser(match);
        onChangeRef.current({ emailHasExistingUser: !!match });
      })
      .catch(() => {
        if (!cancelled) {
          setExistingUser(null);
          onChangeRef.current({ emailHasExistingUser: false });
        }
      })
      .finally(() => { if (!cancelled) setCheckingEmail(false); });
    return () => { cancelled = true; };
  }, [debouncedEmail]);

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Cargá los datos básicos y se la creamos al jugador (le mandamos un link
        por mail para que setee su contraseña).
      </p>

      <div className="flex flex-col gap-2">
        <Label htmlFor={`email-${index}`}>Email</Label>
        <div className="relative">
          <Input
            id={`email-${index}`}
            type="email"
            value={slot.newUserEmail}
            placeholder="jugador@ejemplo.com"
            onChange={(e) => onChange({ newUserEmail: e.target.value })}
            autoComplete="new-password"
          />
          {checkingEmail && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>
        {existingUser && (
          <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <UserAvatar user={existingUser} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-amber-800">
                Este email ya tiene una cuenta en GetMatch
              </p>
              <p className="truncate text-xs text-amber-700">
                {existingUser.name || existingUser.email}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0 border-amber-300 bg-white text-amber-800 hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-0"
              onClick={() => onPick(existingUser)}
            >
              Seleccionar
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor={`name-${index}`}>Nombre completo</Label>
          <Input
            id={`name-${index}`}
            value={slot.newUserName}
            onChange={(e) => onChange({ newUserName: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label>Género</Label>
          <RadioGroup
            value={slot.newUserGender}
            onValueChange={(v) =>
              onChange({ newUserGender: v as ManagerGender })
            }
            className="flex flex-wrap gap-x-4 gap-y-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="MALE" id={`gender-m-${index}`} />
              <Label htmlFor={`gender-m-${index}`}>Masculino</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="FEMALE" id={`gender-f-${index}`} />
              <Label htmlFor={`gender-f-${index}`}>Femenino</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="OTHER" id={`gender-o-${index}`} />
              <Label htmlFor={`gender-o-${index}`}>Otro</Label>
            </div>
          </RadioGroup>
        </div>
      </div>

      {ageRestricted && (
        <div className="flex flex-col gap-2">
          <Label htmlFor={`birthdate-${index}`}>Fecha de nacimiento</Label>
          <Input
            id={`birthdate-${index}`}
            type="date"
            value={slot.newUserBirthdate}
            onChange={(e) => onChange({ newUserBirthdate: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Requerido porque el torneo tiene restricción de edad.
          </p>
        </div>
      )}

      <CategoryPicker
        index={index}
        categories={categories}
        value={slot.categoryId}
        onChange={(categoryId) => onChange({ categoryId })}
      />
    </div>
  );
}

interface CategoryPickerProps {
  index: number;
  categories: EloCategory[];
  value: number | null;
  onChange: (categoryId: number) => void;
}

function CategoryPicker({
  index,
  categories,
  value,
  onChange,
}: CategoryPickerProps) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`category-${index}`}>Categoría</Label>
      <Select
        value={value ? String(value) : undefined}
        onValueChange={(v) => onChange(Number(v))}
      >
        <SelectTrigger id={`category-${index}`}>
          <SelectValue placeholder="Elegí una categoría" />
        </SelectTrigger>
        <SelectContent>
          {categories.map((cat) => (
            <SelectItem key={cat.id} value={String(cat.id)}>
              {cat.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        El ELO inicial se setea en el punto medio de la categoría elegida.
      </p>
    </div>
  );
}
